<template>
  <a-tooltip 
    v-if="isShowTooltip && isPageActive" 
    overlayClassName="tooltip_classMy" 
    :destroyTooltipOnHide="true" 
    v-model:visible="isShowTooltip" 
    placement="top"
  >
    <template #title>
      <span>{{ content }}</span>
    </template>
    <div class="tooltip-content" @mouseleave="handleMouseLeave">
      <span>
        <slot>{{ content }}</slot>
      </span>
    </div>
  </a-tooltip>
  <div v-else class="tooltip-content" @mouseenter="handleMouseEnter" @mouseleave="handleMouseLeave">
    <span ref="contentRef">
      <slot>{{ content }}</slot>
    </span>
  </div>
</template>

<script setup>
import { ref, watch, onUnmounted, nextTick, onDeactivated, onActivated, getCurrentInstance } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const { emit } = getCurrentInstance()

const props = defineProps({
  content: {
    type: String,
    default: ''
  },
  offsetHeight: { type: Number, default: 66 }
})

const contentRef = ref()
const isShowTooltip = ref(false)
const isPageActive = ref(true) // 新增：页面是否活跃状态

// 快速移动检测相关变量
const lastEnterTime = ref(0)
const FAST_MOVE_THRESHOLD = 50
let cleanupTimer = null

// 监听路由变化，在路由变化前就关闭tooltip
let routeChangeCleanup = null

const handleMouseLeave = () => {
  isShowTooltip.value = false
  clearTimers()
  lastEnterTime.value = 0
}

const handleMouseEnter = () => {
  // 如果页面不活跃，不显示tooltip
  if (!isPageActive.value) {
    return
  }
  
  const now = Date.now()
  
  // 快速移动检测
  if (lastEnterTime.value > 0 && (now - lastEnterTime.value) < FAST_MOVE_THRESHOLD) {
    clearAllTooltips()
    lastEnterTime.value = now
    return
  }
  
  lastEnterTime.value = now
  clearTimers()

  // 使用 nextTick 确保 DOM 已渲染
  nextTick(() => {
    if (!contentRef.value) return
    
    const element = contentRef.value;
    // 核心修改：使用 scrollHeight 和 clientHeight 判断内容是否溢出
    const isOverflowing = element.scrollHeight > element.clientHeight;
    
    if (isOverflowing) {
      cleanupTimer = setTimeout(() => {
        // 再次检查页面是否活跃
        if (isPageActive.value) {
          isShowTooltip.value = true;
        }
      }, 50);
    } else {
      isShowTooltip.value = false;
    }
  })
}

// 清理所有tooltip的方法
const clearAllTooltips = () => {
  isShowTooltip.value = false
  clearTimers()
  lastEnterTime.value = 0
}

// 页面失活时的清理
const deactivatePage = () => {
  isPageActive.value = false
  clearAllTooltips()
}

// 页面激活时的恢复
const activatePage = () => {
  isPageActive.value = true
}

// 清理定时器
const clearTimers = () => {
  if (cleanupTimer) {
    clearTimeout(cleanupTimer)
    cleanupTimer = null
  }
  if (routeChangeCleanup) {
    clearTimeout(routeChangeCleanup)
    routeChangeCleanup = null
  }
}

// 监听路由变化
const unsubscribe = router.beforeEach((to, from, next) => {
  // 在路由跳转前就关闭所有tooltip
  deactivatePage()
  next()
})

// 监听路由变化完成
watch(
  () => router.currentRoute.value.path,
  (newValue, oldValue) => {
    // 路由变化完成后，延迟激活页面状态
    routeChangeCleanup = setTimeout(() => {
      activatePage()
    }, 100)
  }
)

onUnmounted(() => {
  clearTimers()
  clearAllTooltips()
  unsubscribe() // 取消路由监听
})

onDeactivated(() => {
  deactivatePage()
})

onActivated(() => {
  activatePage()
})
</script>

<style scoped>
.tooltip-content {
  display: -webkit-box;
  -webkit-line-clamp: 3; /* 限制显示3行 */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.tooltip-content span {
  display: block;
  width: 100%;
}
</style>

<style>
.tooltip_classMy { 
  /* max-width: 400px;  */
}
</style>